import i18next from "i18next";
import { toast } from "react-toastify";

import { globalSystemConstants, eventsConstants } from "../../constants";
import {
  customToast,
  errorHandler,
  convertBytesToMbs,
  calculateTotalFileSizeInMbs,
} from "../../shared";
import { custom, failure } from "./utilities";
import { eventsServices } from "../../services";
import { configurationsActions } from "./configurations.actions";

const {
  GET_EVENTS,
  CANCEL_EVENT,
  ADD_TO_GALLERY,
  GET_EVENT_GALLERY,
  REASSIGN_SHUTTERBS,
  DELETE_FROM_GALLERY,
  VIEW_EVENT_SHUTTERBS,
  FAVORITE_GALLERY_CLIP,
  DOWNLOAD_GALLERY_DATA,
  GET_EVENT_DETAIL_BY_ID,
} = eventsConstants;

const getCompletedEvents = (pageSize, pageNumber, filterExpression) => {
  return (dispatch) => {
    dispatch(custom(GET_EVENTS, globalSystemConstants.SPINNER_ACTIVATE));
    eventsServices
      .getCompletedEvents(pageSize, pageNumber, filterExpression)
      .then(
        (response) => {
          dispatch(custom(response, GET_EVENTS));
        },
        (error) => {
          errorHandler(error);
          dispatch(failure(error, GET_EVENTS));
        }
      )
      .finally(() => {
        dispatch(custom(GET_EVENTS, globalSystemConstants.SPINNER_DIACTIVATE));
      });
  };
};

const getUpcomingEvents = (pageSize, pageNumber, filterExpression) => {
  return (dispatch) => {
    dispatch(custom(GET_EVENTS, globalSystemConstants.SPINNER_ACTIVATE));
    eventsServices
      .getUpcomingEvents(pageSize, pageNumber, filterExpression)
      .then(
        (response) => {
          dispatch(custom(response, GET_EVENTS));
        },
        (error) => {
          errorHandler(error);
          dispatch(failure(error, GET_EVENTS));
        }
      )
      .finally(() => {
        dispatch(custom(GET_EVENTS, globalSystemConstants.SPINNER_DIACTIVATE));
      });
  };
};

const getCanceledEvents = (pageSize, pageNumber, filterExpression) => {
  return (dispatch) => {
    dispatch(custom(GET_EVENTS, globalSystemConstants.SPINNER_ACTIVATE));
    eventsServices
      .getCanceledEvents(pageSize, pageNumber, filterExpression)
      .then(
        (response) => {
          dispatch(custom(response, GET_EVENTS));
        },
        (error) => {
          errorHandler(error);
          dispatch(failure(error, GET_EVENTS));
        }
      )
      .finally(() => {
        dispatch(custom(GET_EVENTS, globalSystemConstants.SPINNER_DIACTIVATE));
      });
  };
};

// Cancel an event and refresh upcoming events
const cancelEvent = (eventGroupId, pageSize, pageNumber, filterExpression, id) => {
  return (dispatch) => {
    dispatch(custom(CANCEL_EVENT, globalSystemConstants.SPINNER_ACTIVATE));
    eventsServices
      .cancelEvent(eventGroupId, id)
      .then(
        (response) => {
          if (response && response.items !== "") {
            customToast(i18next.t(response.items), toast.TYPE.SUCCESS);
          }
          dispatch(getUpcomingEvents(pageSize, pageNumber, filterExpression));
        },
        (error) => {
          errorHandler(error);
          dispatch(failure(error, CANCEL_EVENT));
        }
      )
      .finally(() => {
        dispatch(
          custom(CANCEL_EVENT, globalSystemConstants.SPINNER_DIACTIVATE)
        );
      });
  };
};

// View assigned shutterbs for event
const viewEventShutterbs = (id) => {
  return (dispatch) => {
    dispatch(
      custom(VIEW_EVENT_SHUTTERBS, globalSystemConstants.SPINNER_ACTIVATE)
    );
    eventsServices
      .viewEventShutterbs(id)
      .then(
        (response) => {
          if (response) {
            dispatch(custom(response, VIEW_EVENT_SHUTTERBS));
            dispatch(configurationsActions.getAllShutterBsForReels());
          }
        },
        (error) => {
          errorHandler(error);
          dispatch(failure(error, VIEW_EVENT_SHUTTERBS));
        }
      )
      .finally(() => {
        dispatch(
          custom(VIEW_EVENT_SHUTTERBS, globalSystemConstants.SPINNER_DIACTIVATE)
        );
      });
  };
};

// Reassign shutterbs to event and refresh event list
const resAssignShutterbs = (
  eventId,
  data,
  pageSize,
  pageNumber,
  filterExpression,
  closeModal
) => {
  return (dispatch) => {
    dispatch(
      custom(REASSIGN_SHUTTERBS, globalSystemConstants.SPINNER_ACTIVATE)
    );
    eventsServices
      .resAssignShutterbs(eventId, data)
      .then(
        (response) => {
          if (data.reassignSb && data.reassignSb.length > 0) {
            customToast(i18next.t(response), toast.TYPE.SUCCESS);
          }
        },
        (error) => {
          errorHandler(error);
          dispatch(failure(error, REASSIGN_SHUTTERBS));
        }
      )
      .finally(() => {
        dispatch(
          custom(REASSIGN_SHUTTERBS, globalSystemConstants.SPINNER_DIACTIVATE)
        );
        dispatch(getUpcomingEvents(pageSize, pageNumber, filterExpression));
        closeModal();
      });
  };
};

// Get event gallery
const getEventGallery = (eventId, isActive) => {
  return (dispatch) => {
    dispatch(custom(GET_EVENT_GALLERY, globalSystemConstants.SPINNER_ACTIVATE));
    eventsServices
      .getEventGallery(eventId, isActive)
      .then(
        (response) => {
          dispatch(custom(response, GET_EVENT_GALLERY));
        },
        (error) => {
          errorHandler(error);
          dispatch(failure(error, GET_EVENT_GALLERY));
        }
      )
      .finally(() => {
        dispatch(
          custom(GET_EVENT_GALLERY, globalSystemConstants.SPINNER_DIACTIVATE)
        );
        dispatch(
          custom(GET_EVENT_GALLERY, globalSystemConstants.OVERLAY_DIACTIVATE)
        );
      });
  };
};

// Request backend to email zipped event files
const downloadSelectedFiles = (eventIdsArr) => {
  return (dispatch) => {
    dispatch(
      custom(DOWNLOAD_GALLERY_DATA, globalSystemConstants.SPINNER_ACTIVATE)
    );
    eventsServices
      .downloadSelectedFiles(eventIdsArr)
      .then(
        (message) => {
          toast.success(i18next.t("download_files_email"), {
            autoClose: 5000,
            hideProgressBar: true,
          });
        },
        (error) => {
          errorHandler(error);
        }
      )
      .finally(() => {
        dispatch(
          custom(
            DOWNLOAD_GALLERY_DATA,
            globalSystemConstants.SPINNER_DIACTIVATE
          )
        );
        dispatch(
          custom(
            DOWNLOAD_GALLERY_DATA,
            globalSystemConstants.DOWNLOAD_SPINNER_DIACTIVATE
          )
        );
      });
  };
};

// Delete file from gallery and reload images
const deleteFromGallery = (data, setSelectedFiles, isActive, onSuccess) => {
  return (dispatch) => {
    dispatch(
      custom(DELETE_FROM_GALLERY, globalSystemConstants.SPINNER_ACTIVATE)
    );
    eventsServices
      .deleteFromGallery(data)
      .then(
        (response) => {
          if (onSuccess) {
            onSuccess();
          }
          setSelectedFiles([]);
          customToast(
            isActive === false
              ? "File Restored Successfully"
              : i18next.t("file_successfully_deleted"),
            toast.TYPE.SUCCESS
          );
        },
        (error) => {
          errorHandler(error);
          dispatch(failure(error, DELETE_FROM_GALLERY));
        }
      )
      .finally(() => {
        dispatch(
          custom(DELETE_FROM_GALLERY, globalSystemConstants.SPINNER_DIACTIVATE)
        );
      });
  };
};

// Upload files to gallery
const addToGallery = (
  data,
  options,
  sbUserId = 0,
  counterFilesUploaded,
  setCounterFilesUploaded = null,
  totalUploadedFileSize = null,
  setTotalFileSizeToUpload = null,
  currentFilePrevUploadedSizeRef = null,
  setCurrentFileName = "",
  setCurrentFileSize = "",
  onSuccess
) => {
  const files = Array.from(data.entries()).filter(
    (entry) => entry[0] === "file" && entry[1] instanceof File
  );
  const extractedEventId = data.get("eventId");
  const extractedSbUserId = data.get("SbUserId");
  const extractedUserId = data.get("UserId");
  const contentType = data.get("contentType");

  const images = [];
  const videos = [];

  files.forEach((file) => {
    const fileObject = file[1];
    if (fileObject.type && fileObject.type.startsWith("video/")) {
      videos.push(fileObject);
    } else {
      images.push(fileObject);
    }
  });

  setTotalFileSizeToUpload(
    calculateTotalFileSizeInMbs(videos) + calculateTotalFileSizeInMbs(images)
  );

  return async (dispatch) => {
    try {
      // Make API calls for videos sequentially
      for (const video of videos) {
        currentFilePrevUploadedSizeRef.current = 0;
        setCurrentFileName(video.name);
        setCurrentFileSize(convertBytesToMbs(video.size).toFixed(2));
        const videoFormData = new FormData();
        videoFormData.append(`file`, video);
        videoFormData.append("eventId", extractedEventId);
        videoFormData.append("SbUserId", extractedSbUserId);
        videoFormData.append("UserId", extractedUserId);
        videoFormData.append("contentType", contentType);

        await eventsServices.addToGallery(videoFormData, options);

        if (counterFilesUploaded) {
          setCounterFilesUploaded((prevCount) => prevCount + 1);
        }
      }

      // Make API calls for images sequentially
      for (const image of images) {
        currentFilePrevUploadedSizeRef.current = 0;
        setCurrentFileName(image.name);
        setCurrentFileSize(convertBytesToMbs(image.size).toFixed(2));
        const imageFormData = new FormData();
        imageFormData.append(`file`, image);
        imageFormData.append("eventId", extractedEventId);
        imageFormData.append("SbUserId", extractedSbUserId);
        imageFormData.append("UserId", extractedUserId);
        imageFormData.append("contentType", contentType);

        await eventsServices.addToGallery(imageFormData, options);
        if (counterFilesUploaded) {
          setCounterFilesUploaded((prevCount) => prevCount + 1);
          totalUploadedFileSize += convertBytesToMbs(image.size);
        }
      }

      // Handle success for images and videos
      customToast("Added to gallery successfully", toast.TYPE.SUCCESS);
      // dispatch(success(imagesResponse, ADD_TO_GALLERY));

      const object = {};
      for (let [key, value] of data.entries()) {
        if (key === "SbUserId") {
          key = "sbUserId";
          value = parseInt(value);
        }
        if (key === "eventId") {
        }
        object[key] = value;
      }
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error in addToGallery:", error);
      errorHandler(error);
      dispatch(failure(error, ADD_TO_GALLERY));

      const object = {};
      for (let [key, value] of data.entries()) {
        if (key === "SbUserId") {
          key = "sbUserId";
          value = parseInt(value);
        }
        if (key === "eventId") {
        }
        object[key] = value;
      }
      if (onSuccess) {
        onSuccess();
      }
      throw error;
    }
  };
};

// Fetch event details
const getEventDetailByEventId = (eventId) => {
  return (dispatch) => {
    dispatch(
      custom(GET_EVENT_DETAIL_BY_ID, globalSystemConstants.SPINNER_ACTIVATE)
    );
    eventsServices
      .getEventDetailByEventId(eventId)
      .then(
        (response) => {
          dispatch(custom(response, GET_EVENT_DETAIL_BY_ID));
        },
        (error) => {
          errorHandler(error);
          dispatch(failure(error, GET_EVENT_DETAIL_BY_ID));
        }
      )
      .finally(() => {
        dispatch(
          custom(
            GET_EVENT_DETAIL_BY_ID,
            globalSystemConstants.SPINNER_DIACTIVATE
          )
        );
      });
  };
};

// Toggle favorite status for clip
const favoriteClip = (clipId, getUpdatedData) => {
  return (dispatch) => {
    dispatch(
      custom(FAVORITE_GALLERY_CLIP, globalSystemConstants.SPINNER_ACTIVATE)
    );
    eventsServices
      .favoriteClip(clipId)
      .then(
        (response) => {
          // Call only on success
          if (getUpdatedData) {
            getUpdatedData();
          }
          customToast("Updated Successfully", toast.TYPE.SUCCESS);
        },
        (error) => {
          errorHandler(error);
          dispatch(failure(error, FAVORITE_GALLERY_CLIP));
        }
      )
      .finally(() => {
        dispatch(
          custom(
            FAVORITE_GALLERY_CLIP,
            globalSystemConstants.SPINNER_DIACTIVATE
          )
        );
      });
  };
};

// Get group events gallery
const getGroupEventGallery = (groupEventId, isActive) => {
  return (dispatch) => {
    dispatch(custom(GET_EVENT_GALLERY, globalSystemConstants.SPINNER_ACTIVATE));
    eventsServices
      .getGroupEventGallery(groupEventId, isActive)
      .then(
        (response) => {
          dispatch(custom(response, GET_EVENT_GALLERY));
        },
        (error) => {
          errorHandler(error);
          dispatch(failure(error, GET_EVENT_GALLERY));
        }
      )
      .finally(() => {
        dispatch(
          custom(GET_EVENT_GALLERY, globalSystemConstants.SPINNER_DIACTIVATE)
        );
        dispatch(
          custom(GET_EVENT_GALLERY, globalSystemConstants.OVERLAY_DIACTIVATE)
        );
      });
  };
};

export const eventsActions = {
  cancelEvent,
  addToGallery,
  favoriteClip,
  getEventGallery,
  getUpcomingEvents,
  getCanceledEvents,
  deleteFromGallery,
  viewEventShutterbs,
  resAssignShutterbs,
  getCompletedEvents,
  getGroupEventGallery,
  downloadSelectedFiles,
  getEventDetailByEventId,
};
